import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, AlertCircle, CheckCircle, AlertTriangle, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { API } from '../App';

const VERDICT_STYLES = {
  green: { bg: '#ECFDF5', border: '#10B981', text: '#065F46', icon: CheckCircle },
  amber: { bg: '#FFFBEB', border: '#F59E0B', text: '#92400E', icon: AlertTriangle },
  red: { bg: '#FEF2F2', border: '#DC2626', text: '#991B1B', icon: AlertCircle },
};

const TAG_STYLES = {
  stable_top: { bg: '#ECFDF5', text: '#065F46', icon: Minus, label: 'Stable Top' },
  stable_bottom: { bg: '#F3F4F6', text: '#374151', icon: Minus, label: 'Stable Bottom' },
  riser: { bg: '#DBEAFE', text: '#1E3A8A', icon: ArrowUp, label: 'Riser' },
  faller: { bg: '#FEE2E2', text: '#991B1B', icon: ArrowDown, label: 'Faller' },
  entered_late: { bg: '#FDF2F8', text: '#9D174D', icon: ArrowUp, label: 'Entered Late' },
  exited: { bg: '#F5F5F4', text: '#57534E', icon: ArrowDown, label: 'Exited' },
};

export default function PipelineComparison() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [aId, setAId] = useState('');
  const [bId, setBId] = useState('');
  const [aTraits, setATraits] = useState([]);
  const [bTraits, setBTraits] = useState([]);
  const [traitA, setTraitA] = useState('');
  const [traitB, setTraitB] = useState('');
  const [topK, setTopK] = useState(10);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/analysis-history`, { withCredentials: true })
      .then(r => { if (mounted) setHistory(r.data || []); })
      .catch(() => { if (mounted) setHistory([]); })
      .finally(() => { if (mounted) setLoadingHistory(false); });
    return () => { mounted = false; };
  }, []);

  // Fetch the trait list when an analysis is picked.  The endpoint returns
  // one design per analysis (typical Augmented run) but loops over all if
  // present.  Flatten across designs for the dropdown.
  const _fetchTraitsFor = async (analysisId, setter, resetSelected) => {
    if (!analysisId) {
      setter([]);
      resetSelected('');
      return;
    }
    try {
      const r = await axios.get(`${API}/analyses/${analysisId}/traits`, { withCredentials: true });
      const flat = [];
      (r.data?.designs || []).forEach(d => {
        d.traits.forEach(t => flat.push(t));
      });
      // Dedupe in case multiple designs surface the same trait name
      const unique = Array.from(new Set(flat));
      setter(unique);
      resetSelected('');
    } catch (_) {
      setter([]);
      resetSelected('');
    }
  };

  useEffect(() => { _fetchTraitsFor(aId, setATraits, setTraitA); }, [aId]);
  useEffect(() => { _fetchTraitsFor(bId, setBTraits, setTraitB); }, [bId]);

  const run = async () => {
    if (!aId || !bId) {
      setError('Pick two analyses to compare.');
      return;
    }
    if (aId === bId) {
      setError('Analyses A and B must be different.');
      return;
    }
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const body = {
        analysis_id_a: aId,
        analysis_id_b: bId,
        top_k: Number(topK) || 10,
      };
      if (traitA) body.trait_a = traitA;
      if (traitB) body.trait_b = traitB;
      const r = await axios.post(`${API}/analyses/compare`, body, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setRunning(false);
    }
  };

  const verdictStyle = result ? VERDICT_STYLES[result.verdict] || VERDICT_STYLES.amber : null;
  const VerdictIcon = verdictStyle?.icon;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-cream, #F5F1E8)' }} data-testid="pipeline-comparison-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="text-sm mb-4 text-stone-600 hover:text-stone-900"
          data-testid="back-button"
        >
          ← Back
        </button>
        <div className="flex items-center gap-3 mb-2">
          <ArrowLeftRight size={28} style={{ color: '#2D6A4F' }} />
          <h1 className="text-3xl font-bold" style={{ color: '#1B4F47' }}>
            Pipeline Comparison
          </h1>
        </div>
        <p className="text-stone-600 mb-8 text-sm">
          Compare BLUP rankings of two analyses (typically two seasons or two locations) to
          measure cross-trial stability. Citations: Falconer 1996 §19.5; Kang 1988.
        </p>

        {/* Selectors */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6 shadow-sm">
          <h2 className="font-semibold text-stone-800 mb-4">Select two analyses</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Analysis A (e.g. Season 1)', value: aId, set: setAId, testid: 'select-analysis-a' },
              { label: 'Analysis B (e.g. Season 2)', value: bId, set: setBId, testid: 'select-analysis-b' },
            ].map((cfg) => (
              <div key={cfg.testid}>
                <label className="block text-xs font-semibold text-stone-600 mb-2">{cfg.label}</label>
                <select
                  value={cfg.value}
                  onChange={(e) => cfg.set(e.target.value)}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                  data-testid={cfg.testid}
                  disabled={loadingHistory}
                >
                  <option value="">
                    {loadingHistory ? 'Loading history...' : '— pick one —'}
                  </option>
                  {history.map(h => (
                    <option key={h.id} value={h.id}>
                      {h.filename} · {h.analysis_type} · {new Date(h.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {/* Trait pickers — shown once an analysis is selected; "first
              available" is the default so the field is optional. */}
          {(aId || bId) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-2">
                  Trait for A {aTraits.length === 0 && aId ? '(loading...)' : '(optional)'}
                </label>
                <select
                  value={traitA}
                  onChange={(e) => setTraitA(e.target.value)}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="select-trait-a"
                  disabled={!aId || aTraits.length === 0}
                >
                  <option value="">— first available —</option>
                  {aTraits.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-2">
                  Trait for B {bTraits.length === 0 && bId ? '(loading...)' : '(optional)'}
                </label>
                <select
                  value={traitB}
                  onChange={(e) => setTraitB(e.target.value)}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="select-trait-b"
                  disabled={!bId || bTraits.length === 0}
                >
                  <option value="">— first available —</option>
                  {bTraits.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="flex items-end gap-4 mt-4">
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-2">Top-K selection size</label>
              <input
                type="number"
                min="1"
                max="50"
                value={topK}
                onChange={(e) => setTopK(e.target.value)}
                className="w-24 border border-stone-300 rounded-lg px-3 py-2 text-sm"
                data-testid="top-k-input"
              />
            </div>
            <button
              onClick={run}
              disabled={running || !aId || !bId}
              className="px-6 py-2 rounded-lg font-semibold text-white disabled:opacity-50"
              style={{ background: '#2D6A4F' }}
              data-testid="run-comparison-button"
            >
              {running ? 'Comparing...' : 'Compare Pipelines'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-300 text-rose-800 p-4 rounded-lg mb-6" data-testid="comparison-error">
            <strong>Comparison failed: </strong>{error}
          </div>
        )}

        {result && (
          <>
            {/* Verdict banner */}
            <div
              className="rounded-xl p-6 mb-6 border-2"
              style={{ background: verdictStyle.bg, borderColor: verdictStyle.border }}
              data-testid="verdict-banner"
            >
              <div className="flex items-start gap-3">
                <VerdictIcon size={28} style={{ color: verdictStyle.border, flexShrink: 0 }} />
                <div>
                  <div className="font-bold text-lg" style={{ color: verdictStyle.text }}>
                    {result.verdict_label.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  <div className="mt-1 text-sm" style={{ color: verdictStyle.text }}>
                    {result.verdict_copy}
                  </div>
                </div>
              </div>
            </div>

            {/* Numerical summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Spearman ρ', value: result.spearman_rho?.toFixed(4) ?? '—' },
                { label: 'Pearson r', value: result.pearson_r?.toFixed(4) ?? '—' },
                { label: `Top-${result.top_k} Intersection`, value: `${result.top_k_intersection}/${result.top_k} (${result.top_k_intersection_pct}%)` },
                { label: 'Common Entries', value: result.entries_common_count },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-lg border border-stone-200 p-4">
                  <div className="text-xs text-stone-500 mb-1">{s.label}</div>
                  <div className="text-xl font-bold text-stone-900">{s.value}</div>
                </div>
              ))}
            </div>

            {/* Common-set table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 bg-stone-50">
                <h3 className="font-semibold text-stone-800">Per-Entry Movements</h3>
                <p className="text-xs text-stone-500 mt-1">
                  A = {result.filename_a} · {result.analysis_a.design_label} · trait {result.analysis_a.trait}
                  &nbsp;·&nbsp;
                  B = {result.filename_b} · {result.analysis_b.design_label} · trait {result.analysis_b.trait}
                </p>
              </div>
              <div className="overflow-x-auto" data-testid="per-entry-table">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-700">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Entry</th>
                      <th className="px-4 py-2 text-right font-semibold">Rank A</th>
                      <th className="px-4 py-2 text-right font-semibold">Rank B</th>
                      <th className="px-4 py-2 text-right font-semibold">Δ Rank</th>
                      <th className="px-4 py-2 text-right font-semibold">BLUP A</th>
                      <th className="px-4 py-2 text-right font-semibold">BLUP B</th>
                      <th className="px-4 py-2 text-left font-semibold">Tag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.per_entry.map((r, i) => {
                      const tagStyle = TAG_STYLES[r.tag] || TAG_STYLES.stable_bottom;
                      const TagIcon = tagStyle.icon;
                      return (
                        <tr key={r.Entry} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                          <td className="px-4 py-2 font-mono">{r.Entry}</td>
                          <td className="px-4 py-2 text-right">{r.rank_A ?? '—'}</td>
                          <td className="px-4 py-2 text-right">{r.rank_B ?? '—'}</td>
                          <td className="px-4 py-2 text-right">
                            {r.rank_delta == null ? '—' : (r.rank_delta > 0 ? `+${r.rank_delta}` : r.rank_delta)}
                          </td>
                          <td className="px-4 py-2 text-right">{r.BLUP_A?.toFixed(2) ?? '—'}</td>
                          <td className="px-4 py-2 text-right">{r.BLUP_B?.toFixed(2) ?? '—'}</td>
                          <td className="px-4 py-2">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{ background: tagStyle.bg, color: tagStyle.text }}
                            >
                              <TagIcon size={11} /> {tagStyle.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Citations footer */}
            <div className="mt-6 text-xs text-stone-500 italic">
              Methodology: {result.citations.join(' · ')}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
